import { ReactNode, useCallback, useLayoutEffect, useRef } from 'react';

/* Clearance kept below the slide content so the page never grows past the
   viewport: at >=1080px the QuizContainer + QuizSlidesContainer trailing
   paddings total 140px (which also clears the 100px fixed BottomNav); below
   that the mobile fixed nav plus safe-area inset needs ~110px. */
const reservedBottom = () => (window.innerWidth >= 1080 ? 140 : 110);

/* The FramedCover backdrop photo is pinned at 100px from the viewport top
   on desktop; slide text must always keep at least 30px of free space to
   that image's top edge, so a scaled slide is never allowed to start
   higher than cover top (100) + clearance (30) from the viewport top. */
const MIN_TOP = 130;

/* Below this scale text stops being readable from a distance, so the page is
   allowed to scroll again rather than shrink further. */
const MIN_SCALE = 0.3;

interface IFitToViewportProps {
	children: ReactNode;
	/** Top-anchored screens (/play, /mode): content hugs the container top
	    and stays put when shrunk, so the MIN_TOP clamp and the rendered-top
	    correction loop (both built for bottom-anchored slides, where
	    shrinking moves the top edge down) would only eat height budget —
	    measure from the real anchor top and skip the loop instead. */
	topAligned?: boolean;
}

/**
 * Fits a quiz slide into the viewport without scrolling: when the natural
 * content height overflows the space between the slide top and the bottom
 * nav, the content is shrunk via CSS zoom — fonts and images get smaller and
 * text re-wraps into the full container width, while the slide block itself
 * keeps its width and structure (unlike transform scaling, which would shrink
 * the whole block and leave dead space around it). Content that fits renders
 * untouched at scale 1.
 */
export const FitToViewport = ({
	children,
	topAligned = false,
}: IFitToViewportProps) => {
	const outerRef = useRef<HTMLDivElement>(null);
	const innerRef = useRef<HTMLDivElement>(null);

	const measure = useCallback(() => {
		const outer = outerRef.current;
		const inner = innerRef.current;
		if (!outer || !inner) {
			return;
		}

		/* Measure from the slides CONTAINER top (stable below the navigator),
		   not the outer wrapper: the wrapper bottom-anchors via margin-top:auto,
		   so its own top would move with the content height and feed back into
		   the fit. Document-relative so mid-scroll measurement stays correct. */
		const anchor = outer.parentElement ?? outer;
		const anchorTop = anchor.getBoundingClientRect().top + window.scrollY;
		/* Since the slide bottom-anchors, shrinking `available` moves its top
		   edge DOWN — clamping the top to MIN_TOP is what guarantees the free
		   band above the text. An anchor already below MIN_TOP keeps its own,
		   larger clearance. */
		const top = topAligned ? anchorTop : Math.max(anchorTop, MIN_TOP);
		let available = Math.max(window.innerHeight - top - reservedBottom(), 1);

		/* Always measure the natural height with the zoom (and the chrome
		   counter-zoom, see below) neutralized: at smaller zoom the text
		   re-wraps and the height changes, so measuring at the current zoom
		   would feed back into itself. No paint can happen in the middle of
		   this callback, so the reset is never visible. */
		inner.style.setProperty('zoom', '1');
		inner.style.setProperty('--fit-zoom', '1');
		inner.style.setProperty('--fit-unzoom', '1');
		const natural = inner.offsetHeight;
		if (natural <= 0) {
			inner.style.removeProperty('zoom');
			inner.style.removeProperty('--fit-zoom');
			inner.style.removeProperty('--fit-unzoom');
			return;
		}
		let scale = 1;

		/* Two effects make the final height differ from scale·natural, so the
		   scale is solved against the real zoomed layout instead of computed
		   once. (1) Elements styled with zoom: var(--fit-unzoom) (the
		   BODOVI/ODGOVOR labels and the points circle) shrink at a DAMPED
		   rate — the square root of the slide zoom — so they take up MORE
		   local space the smaller the zoom gets. (2) A zoomed-out slide lays
		   out in a WIDER effective space, so text re-wraps into fewer lines
		   and the measured height moves in whole-line JUMPS. Those jumps mean
		   a fixed-point iteration (scale = target/height, remeasure, repeat)
		   can cycle between the two sides of a wrap boundary and exit on the
		   far-too-small side, stranding a big empty band that the container's
		   stage spacers then park above the slide. A bracketed bisection
		   has no such failure mode: "does scale s fit?" is monotone, so it
		   deterministically lands on the LARGEST scale that still fits. Each
		   probe applies the candidate zoom and measures (offsetHeight is in
		   the zoomed element's own units, so visual height = scale·local);
		   a few px of slack absorb the residual so the fit never leaves a
		   1-2px scrollbar. */
		const apply = (s: number) => {
			inner.style.setProperty('zoom', String(s));
			inner.style.setProperty('--fit-zoom', String(s));
			inner.style.setProperty('--fit-unzoom', String(Math.sqrt(1 / s)));
		};
		const solve = () => {
			if (natural <= available && scale === 1) {
				return;
			}
			const target = Math.max(available - 4, 1);
			let lo = MIN_SCALE;
			let hi = Math.min(scale, 1);
			for (let i = 0; i < 12 && hi - lo > 0.002; i++) {
				const mid = (lo + hi) / 2;
				apply(mid);
				if (mid * inner.offsetHeight <= target) {
					lo = mid;
				} else {
					hi = mid;
				}
			}
			/* lo is the largest probed scale known to fit (or MIN_SCALE, below
			   which the page is allowed to scroll instead). */
			scale = lo;
			apply(scale);
		};
		solve();

		/* reservedBottom is an estimate; when the chrome under the slide is in
		   fact taller, the bottom-anchored slide rides up above the clamped top.
		   Verify the RENDERED top and tighten the height budget by the
		   shortfall — the slide top moves down 1:1 with the removed space, so a
		   couple of passes settle it. This also catches slides that fit
		   naturally but still poke into the reserved top band. */
		for (let pass = 0; pass < 3 && !topAligned && scale > MIN_SCALE; pass++) {
			const rectTop = inner.getBoundingClientRect().top + window.scrollY;
			const shortfall = top - rectTop;
			if (shortfall <= 1) {
				break;
			}
			available = Math.max(available - shortfall, 1);
			scale = Math.min(scale, 0.999);
			solve();
		}

		/* Styles are written straight to the DOM (not React state) so every
		   measurement settles before paint with no re-render. The chrome ends
		   up at sqrt(scale) visually: --fit-unzoom (inside the zoomed slide)
		   carries sqrt(1/scale). --fit-zoom carries the raw scale for styles
		   that mix viewport units with local lengths (vw is not multiplied by
		   the zoom, so full-bleed calcs divide their vw term by this). */
		if (scale < 1) {
			inner.style.setProperty('zoom', String(scale));
			inner.style.setProperty('--fit-zoom', String(scale));
			inner.style.setProperty('--fit-unzoom', String(Math.sqrt(1 / scale)));
		} else {
			inner.style.removeProperty('zoom');
			inner.style.removeProperty('--fit-zoom');
			inner.style.removeProperty('--fit-unzoom');
		}
	}, [topAligned]);

	/* No dependency array: every commit (question change, mode change) is
	   re-measured before the browser paints, so a slide never flashes at the
	   previous slide's scale. */
	useLayoutEffect(measure);

	useLayoutEffect(() => {
		const inner = innerRef.current;
		if (!inner) {
			return;
		}

		/* The first paint can happen before the quiz web fonts finish loading;
		   fallback-font metrics then measure short and the slide visibly
		   shrinks once the real fonts land. Hold the slide invisible until the
		   fonts are in and remeasure, so the first thing the player sees is
		   already at its final scale. Fonts are cached after the first slide,
		   making this a no-op for the rest of the quiz. */
		let mounted = true;
		if (document.fonts.status !== 'loaded') {
			inner.style.visibility = 'hidden';
			document.fonts.ready.then(() => {
				if (mounted) {
					measure();
					inner.style.visibility = '';
				}
			});
		}

		/* Async size changes after paint (an image swapping in for its
		   skeleton, a font substitution) re-fit through the observer. */
		const observer = new ResizeObserver(measure);
		observer.observe(inner);
		window.addEventListener('resize', measure);
		return () => {
			mounted = false;
			observer.disconnect();
			window.removeEventListener('resize', measure);
		};
	}, [measure]);

	return (
		<div ref={outerRef}>
			{/* flow-root keeps children's top/bottom margins inside this box —
			    a margin collapsing through the edge would escape offsetHeight
			    and make the fit under-measure by that margin. */}
			<div ref={innerRef} style={{ display: 'flow-root' }}>
				{children}
			</div>
		</div>
	);
};
